import API from '@/api/apiCreate'
import type { ResponseWrapper } from '@/api/types/base.types'
import type { AxiosRequestConfig } from 'axios'
import {
  CRM_INTEGRATION_CHAT_DEFAULT_PER_PAGE,
  CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT,
  type CrmIntegrationChatUnreadCountData,
  type CrmIntegrationChatGetChatsParams,
  type CrmIntegrationChatGetMessagesParams,
  type CrmIntegrationChatListData,
  type CrmIntegrationChatMarkReadRequest,
  type CrmIntegrationChatMarkReadResponse,
  type CrmIntegrationChatMessagesData,
  type CrmIntegrationChatSendRequest,
  type CrmIntegrationSendResponse,
} from '../types/crm-integration-chat.types'

type ApiRequestConfig = AxiosRequestConfig & {
  skipGlobalErrorAlert?: boolean
}

const SKIP_GLOBAL_ERROR_ALERT_CONFIG: ApiRequestConfig = {
  skipGlobalErrorAlert: true,
}
const buildIntegrationMessagesQuery = ({
  conversation_id,
  limit = CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT,
  offset = 0,
}: CrmIntegrationChatGetMessagesParams): string => {
  const params = new URLSearchParams({
    conversation_id: String(conversation_id),
    limit: String(limit),
    offset: String(offset),
  })
  return params.toString()
}

const buildIntegrationSendBody = (
  body: CrmIntegrationChatSendRequest,
): Record<string, unknown> => {
  const text = body.text ?? body.message ?? ''
  const payload: Record<string, unknown> = {
    conversation_id: body.conversation_id,
    text,
  }

  if (Array.isArray(body.attachments) && body.attachments.length > 0) {
    payload.attachments = body.attachments
  }

  return payload
}

export const crmIntegrationChatApi = {
  /** GET /v1/crm-integration-chat/unread-count */
  getUnreadCount: () =>
    API.get<ResponseWrapper<CrmIntegrationChatUnreadCountData>>(
      'crm-integration-chat/unread-count',
    ),

  /** GET /v1/crm-integration-chat — список CRM-диалогов (VK / TG / MAX / Wazzup) */
  getIntegrationChats: ({
    page = 1,
    perPage = CRM_INTEGRATION_CHAT_DEFAULT_PER_PAGE,
    integrationId,
    channelId,
    managerId,
    search,
    provider,
    leadId,
    conversationId,
    skipGlobalErrorAlert,
  }: CrmIntegrationChatGetChatsParams = {}) => {
    const params = new URLSearchParams({
      page: String(page),
      'per-page': String(perPage),
    })
    if (
      integrationId != null &&
      Number.isFinite(Number(integrationId)) &&
      Number(integrationId) > 0
    ) {
      params.set('integration_id', String(integrationId))
    }
    const channelIdTrimmed =
      channelId == null ? '' : String(channelId).trim()
    if (channelIdTrimmed) {
      params.set('channel_id', channelIdTrimmed)
    }
    if (managerId != null && Number.isFinite(Number(managerId))) {
      const normalizedManagerId = Math.floor(Number(managerId))
      if (normalizedManagerId >= 0) {
        params.set('manager_id', String(normalizedManagerId))
      }
    }
    const providerTrimmed = provider?.trim()
    if (providerTrimmed) {
      params.set('provider', providerTrimmed)
    }
    if (
      leadId != null &&
      Number.isFinite(Number(leadId)) &&
      Number(leadId) > 0
    ) {
      params.set('lead_id', String(leadId))
    }
    if (
      conversationId != null &&
      Number.isFinite(Number(conversationId)) &&
      Number(conversationId) > 0
    ) {
      params.set('conversation_id', String(conversationId))
    }
    const searchTrimmed = search?.trim()
    if (searchTrimmed) {
      params.set('search', searchTrimmed)
    }
    return API.get<ResponseWrapper<CrmIntegrationChatListData>>(
      `crm-integration-chat?${params.toString()}`,
      skipGlobalErrorAlert ? SKIP_GLOBAL_ERROR_ALERT_CONFIG : undefined,
    )
  },

  /** GET /v1/crm-integration-chat?provider=&lead_id= — сводка / диалоги лида */
  getIntegrationChatByLead: ({
    provider,
    leadId,
    conversationId,
    integrationId,
    perPage = 20,
    skipGlobalErrorAlert,
  }: {
    provider: string
    leadId: number
    conversationId?: number | null
    integrationId?: number | null
    perPage?: number
    skipGlobalErrorAlert?: boolean
  }) =>
    crmIntegrationChatApi.getIntegrationChats({
      provider,
      leadId,
      conversationId: conversationId ?? undefined,
      integrationId: integrationId ?? undefined,
      page: 1,
      perPage,
      skipGlobalErrorAlert,
    }),

  /** GET /v1/crm-integration-chat/messages?conversation_id= */
  getIntegrationMessages: (params: CrmIntegrationChatGetMessagesParams) =>
    API.get<ResponseWrapper<CrmIntegrationChatMessagesData>>(
      `crm-integration-chat/messages?${buildIntegrationMessagesQuery(params)}`,
    ),

  /** POST /v1/crm-integration-chat/send — текст и/или unified attachments */
  sendIntegrationMessage: (body: CrmIntegrationChatSendRequest) =>
    API.post<ResponseWrapper<CrmIntegrationSendResponse>>(
      'crm-integration-chat/send',
      buildIntegrationSendBody(body),
    ),

  /** POST /v1/crm-integration-chat/mark-read */
  markIntegrationRead: (body: CrmIntegrationChatMarkReadRequest) =>
    API.post<ResponseWrapper<CrmIntegrationChatMarkReadResponse>>(
      'crm-integration-chat/mark-read',
      body,
    ),
}
